import React, { ChangeEvent, useEffect, useState } from 'react';
import {
  Table as MuiTable,
  TableBody,
  TableRow,
  TableCell,
  TableHead,
  TablePagination,
  Backdrop,
  LinearProgress,
} from '@material-ui/core';
import TablePaginationActions from '@material-ui/core/TablePagination/TablePaginationActions';
import useTableStyle, { useTableCellStyle, useTableRowStyle } from './style';
import { defaultTdStr, isUndefined, timerDebounce } from './consts';
import TableProps, { Iobject, TablePaginationProps } from './type';
import CheckBox from './Checkbox';

/**
 * 1. 若需使用 繁體中文 zhTW, 需於引入，
 *    const theme = createMuiTheme({}, zhTW)，<ThemeProvider theme={theme}>{...}</ThemeProvider>
 * 2. data 自動插入項次 itemNo, 可直接於 column 使用此欄位
 * 3. selectable 開啟(default: true)且資料內需有 [selectedField] 的欄位(default: 'selected')，才有選取 row 的 style
 * 4. page, pageSize 可由外部控制, 內部用 useEffect 同步
 * 5. serverRender 模式, 記得傳入 page, pageSize, count, onPageChange 並設定 serverRender={true}
 * 6. serverRender 模式, page, pageSize 先內部更新，timerDebouce 後傳給 onPageChange
 * 7. onPageChange 一定要於 func 內下 await 才會有 loading
 */
const NewTable = <T extends { id: string | number }>(
  props: TableProps<T>
): JSX.Element => {
  const classes = useTableStyle();
  const rowClasses = useTableRowStyle();
  const cellClasses = useTableCellStyle();

  const {
    serverRender = false,
    size = 'small',
    columns,
    data,
    onDataChange,
    selectedField = 'selected',
    renderHeader,
    renderCell,
    loading: loadingTemp = false,
    selectable = true,
    /** pagenation */
    page: pageTemp = 0,
    pageSize: pageSizeTemp = 10,
    count = data.length,
    pageSizeOptions: rowsPerPageOptions = [10, 20, 50],
    onPageChange,
    progress = <LinearProgress color="secondary" style={{ width: '60%' }} />,
    ...other
  } = props;
  const [rows, setRows] = useState<T[]>([]);
  const [page, setPage] = useState(pageTemp);
  const [pageSize, setPageSize] = useState(pageSizeTemp);
  const [loading, setLoading] = useState(loadingTemp);

  const rowStart = serverRender ? 0 : page * pageSize;
  const rowEnd = serverRender ? data.length : (page + 1) * pageSize - 1;

  // #region 更新資料
  useEffect(() => {
    // 插入項次(itemNo)
    setRows(
      (data || [])
        .filter((obj, idx) => idx >= rowStart && idx <= rowEnd)
        .map((obj, idx) => ({
          ...obj,
          itemNo: rowStart + idx + 1,
        }))
    );
  }, [data, rowStart, rowEnd]);
  // #endregion

  // #region page, pageSize 監聽

  /** 外部 page 改變時，同步更新 page */
  useEffect(() => {
    setPage(pageTemp);
  }, [pageTemp]);

  /** 外部 pageSize 改變時，同步更新 pageSize */
  useEffect(() => {
    setPageSize(pageSizeTemp);
  }, [pageSizeTemp]);
  // #endregion

  /** 外部 pageSize 改變時，同步更新 pageSize */
  useEffect(() => {
    setLoading(loadingTemp);
  }, [loadingTemp]);
  // #endregion

  /** pagenation */
  const handleChangePage: TablePaginationProps['onChangePage'] = async (
    e,
    _page
  ) => {
    setPage(_page);
    if (onPageChange) {
      timerDebounce(async () => {
        setLoading(true);
        await onPageChange(_page, pageSize);
        setLoading(false);
      }, 600);
    }
  };
  const handleChangeRowsPerPage = async (
    e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => {
    const value = parseFloat(e.target.value);
    const maxPage = Math.floor(count / value);
    let pageTT = page;
    if (pageTT > maxPage) {
      pageTT = maxPage;
      setPage(pageTT);
    }

    const pageSizeTT = value;
    setPageSize(pageSizeTT);
    if (onPageChange) {
      setLoading(true);
      await onPageChange(pageTT, pageSizeTT);
      setLoading(false);
    }
  };

  // 重製 selectionCheckbox (header)
  const selectedHeader: TableProps<T>['renderHeader'] = (param) => {
    const { column } = param;
    const currentRows = rows;
    const someTrue = currentRows.some(
      (obj) => (obj as Iobject)[selectedField] === true
    );
    const somefalse = currentRows.some(
      (obj) => (obj as Iobject)[selectedField] === false
    );

    return (
      <CheckBox
        color="secondary"
        label={column.t}
        checked={someTrue}
        indeterminate={someTrue && somefalse}
        ckeckboxStyle={{ color: 'inherit', padding: 4 }}
        labelStyle={{ fontSize: 'inherit', fontWeight: 'inherit' }}
        onChange={(e) => {
          const { checked } = e.target;

          const dataValue = (obj: T) =>
            isUndefined((obj as Iobject)[selectedField]) ? undefined : checked;

          const updateRows = rows.map((obj) => ({
            ...obj,
            [selectedField]: dataValue(obj),
          }));
          setRows(updateRows);

          if (onDataChange) {
            onDataChange(updateRows, rowStart, rowEnd);
          }
        }}
      />
    );
  };

  // 重製 selectionCheckbox (cell))
  const selectedCell: TableProps<T>['renderCell'] = (param) => {
    const { row, rowIndex } = param;

    return (
      <>
        {/** 該欄位 undefined 時，不會產生 checkbox */}
        {!isUndefined((row as Iobject)[selectedField]) && (
          <CheckBox
            color="secondary"
            ckeckboxStyle={{ padding: 4 }}
            checked={(row as Iobject)[selectedField] as boolean}
            onChange={(e) => {
              const { checked } = e.target;
              setRows((prev) =>
                prev.map((obj, idx) => ({
                  ...obj,
                  [selectedField]:
                    idx === rowIndex
                      ? checked
                      : (obj as Iobject)[selectedField],
                }))
              );
            }}
          />
        )}
      </>
    );
  };

  // TODO selectedField
  const renderTh: TableProps<T>['renderHeader'] = (param) => {
    const { column, value } = param;
    return column.f === selectedField ? selectedHeader(param) : value;
  };

  const handleRowSelect = (rowIndex: number) => () => {
    if (isUndefined((rows[rowIndex] as Iobject)[selectedField])) {
      return;
    }

    if (selectable) {
      const dataValue = (obj: T) => (obj as Iobject)[selectedField];

      setRows((prev) =>
        prev.map((obj, idx) => ({
          ...obj,
          [selectedField]: idx === rowIndex ? !dataValue(obj) : dataValue(obj),
        }))
      );

      if (onDataChange) {
        const updateRow = {
          ...rows[rowIndex],
          [selectedField]: !dataValue(rows[rowIndex]),
        };
        onDataChange([updateRow], rowStart + rowIndex, rowStart + rowIndex);
      }
    }
  };

  return (
    <div style={{ position: 'relative' }}>
      <Backdrop
        open={loading}
        style={{
          zIndex: 1,
          position: 'absolute',
          backgroundColor: 'rgba(255, 255, 255, 0.7)',
        }}
      >
        {progress}
      </Backdrop>
      <div style={{ overflow: 'auto' }}>
        <MuiTable size={size} classes={classes} {...other}>
          <TableHead>
            <TableRow>
              {columns.map((column) => {
                const {
                  t,
                  w,
                  f,
                  trans,
                  show = true,
                  thStyle = {},
                  ...other2
                } = column;
                return !show ? (
                  <></>
                ) : (
                  <TableCell
                    key={f}
                    classes={cellClasses}
                    {...other2}
                    style={{ width: w, ...thStyle }}
                  >
                    {renderHeader
                      ? renderHeader({ value: t, column, rows }) ||
                        renderTh({ value: t, column, rows })
                      : renderTh({ value: t, column, rows })}
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {!rows.length ? (
              <TableRow hover className="odd" classes={rowClasses}>
                <TableCell
                  colSpan={columns.filter(({ show = true }) => show).length}
                  align="center"
                  classes={cellClasses}
                >
                  {!data.length ? '查無資料' : '操作錯誤'}
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row, rowIndex) => (
                <TableRow
                  key={row.id}
                  hover
                  className={rowIndex % 2 ? 'even' : 'odd'}
                  classes={rowClasses}
                  selected={(row as unknown as Iobject<boolean>)[selectedField]}
                  onClick={handleRowSelect(rowIndex)}
                >
                  {columns.map((column) => {
                    const {
                      f: field,
                      show = true,
                      align = 'center',
                      tdStyle = {},
                    } = column;
                    const param = {
                      field,
                      value: (row as Iobject)[field] as string,
                      row,
                      column,
                      rows,
                      rowIndex,
                    };
                    const defaultTd =
                      field === selectedField
                        ? selectedCell(param)
                        : defaultTdStr(param);
                    return !show ? (
                      <></>
                    ) : (
                      <TableCell
                        key={row.id + field}
                        align={align}
                        classes={cellClasses}
                        style={tdStyle}
                      >
                        {renderCell
                          ? renderCell({
                              field,
                              value: (row as Iobject)[field] as string,
                              row,
                              column,
                              rows,
                              rowIndex: rowIndex + rowStart, // dataIndex
                            }) || defaultTd
                          : defaultTd}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))
            )}
          </TableBody>
        </MuiTable>
      </div>
      <TablePagination
        component="div"
        colSpan={3}
        count={count}
        page={page}
        rowsPerPage={pageSize}
        rowsPerPageOptions={rowsPerPageOptions}
        SelectProps={{
          inputProps: { 'aria-label': 'rows per page' },
          native: true,
        }}
        onChangePage={handleChangePage}
        onChangeRowsPerPage={handleChangeRowsPerPage}
        ActionsComponent={TablePaginationActions}
      />
    </div>
  );
};

export default NewTable;
